import { useState, useEffect } from "react";
import axios from "axios";
import { ADVANCED_TECHNIQUES } from "@/data/advanced_techniques";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, BookOpen, ShieldAlert, LogIn, LogOut as LogOutIcon, Clock, Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SubNav({ active, setActive, lang }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
      {ADVANCED_TECHNIQUES.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${
              isActive
                ? `bg-gradient-to-r ${t.color} text-white border-transparent shadow-lg`
                : "bg-white text-zinc-700 border-pink-200 hover:border-pink-400"
            }`}
            data-testid={`advanced-tab-${t.id}`}
          >
            <Icon className="w-4 h-4" />
            <span>{lang === "ar" ? t.name.ar : t.name.en}</span>
          </button>
        );
      })}
    </div>
  );
}

function SignalCard({ data, loading, error, technique, lang, onRefresh, symbol, timeframe }) {
  const isAr = lang === "ar";
  const sig = data?.signal || "NEUTRAL";
  const sigColor = sig === "BUY" ? "bg-emerald-500" : sig === "SELL" ? "bg-red-500" : "bg-zinc-400";
  const SigIcon = sig === "BUY" ? TrendingUp : sig === "SELL" ? TrendingDown : Minus;

  return (
    <Card className="bg-white border-4 border-pink-300 rounded-3xl overflow-hidden shadow-lg shadow-pink-200/40">
      <div className={`h-2 bg-gradient-to-r ${technique.color}`} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-pink-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">{isAr ? "الإشارة المباشرة" : "Live Signal"}</h3>
            <Badge variant="outline" className="text-[10px] border-pink-300 text-pink-600">{symbol} · {timeframe}</Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading} className="text-pink-500 h-8 w-8">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center py-8 text-pink-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-sm">{isAr ? "جاري التحليل..." : "Analyzing..."}</span>
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs">{error}</div>}

        {data && (
          <>
            <div className="flex items-center justify-between mb-4">
              <Badge className={`${sigColor} text-white text-base sm:text-lg px-4 py-2 rounded-full font-bold`}>
                <SigIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />
                {sig}
              </Badge>
              <div className="text-right">
                <p className="text-[10px] uppercase text-zinc-500 tracking-wide">{isAr ? "الثقة" : "Confidence"}</p>
                <p className="text-xl sm:text-2xl font-bold text-pink-600">{data.confidence}%</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3">
              <Box label={isAr ? "السعر الحالي" : "Current"} value={data.current_price} color="zinc" />
              <Box label="ATR" value={data.atr} color="zinc" />
              <Box label={isAr ? "الإشارة" : "Signal"} value={data.signal} color={sig === "BUY" ? "emerald" : sig === "SELL" ? "red" : "zinc"} />
              <Box label={isAr ? "الثقة" : "Confidence"} value={`${data.confidence}%`} color="blue" />
            </div>

            {/* Dual ATR-based entries */}
            {(data.entry_aggressive || data.entry_conservative) && (
              <div className="space-y-2 mb-3">
                {data.entry_aggressive && (
                  <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-2.5" data-testid="combo-entry-aggressive">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-amber-700">{isAr ? "دخول جريء (0.3×ATR)" : "Aggressive (0.3×ATR)"}</span>
                      <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">{isAr ? "مبكر" : "Early"}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                      <div><span className="text-zinc-500 block">Entry</span><span className="font-mono font-bold text-zinc-800">{data.entry_aggressive.price}</span></div>
                      <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-600">{data.entry_aggressive.stop_loss}</span></div>
                      <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-emerald-600">{data.entry_aggressive.take_profit_1}</span></div>
                      <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-emerald-600">{data.entry_aggressive.take_profit_2}</span></div>
                    </div>
                  </div>
                )}
                {data.entry_conservative && (
                  <div className="border-2 border-cyan-300 bg-cyan-50 rounded-xl p-2.5" data-testid="combo-entry-conservative">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-cyan-700">{isAr ? "دخول متحفظ (0.7×ATR)" : "Conservative (0.7×ATR)"}</span>
                      <Badge variant="outline" className="text-[9px] border-cyan-400 text-cyan-700">{isAr ? "آمن" : "Safer"}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                      <div><span className="text-zinc-500 block">Entry</span><span className="font-mono font-bold text-zinc-800">{data.entry_conservative.price}</span></div>
                      <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-600">{data.entry_conservative.stop_loss}</span></div>
                      <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-emerald-600">{data.entry_conservative.take_profit_1}</span></div>
                      <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-emerald-600">{data.entry_conservative.take_profit_2}</span></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Technique-specific extras */}
            {data.pattern && <p className="text-xs text-purple-700 mb-1"><strong>{isAr ? "النمط:" : "Pattern:"}</strong> {data.pattern}</p>}
            {data.phase && <p className="text-xs text-purple-700 mb-1"><strong>{isAr ? "المرحلة:" : "Phase:"}</strong> {data.phase}</p>}
            {data.structure && data.structure !== "Unknown" && <p className="text-xs text-purple-700 mb-1"><strong>{isAr ? "الهيكل:" : "Structure:"}</strong> {data.structure}</p>}
            {data.price_vs_cloud && (
              <p className="text-xs text-purple-700 mb-1"><strong>{isAr ? "السعر مقابل السحابة:" : "vs Cloud:"}</strong> {data.price_vs_cloud}
                {data.tenkan && <span className="ml-2">| Tenkan: {data.tenkan} · Kijun: {data.kijun}</span>}
              </p>
            )}
            {data.ratios && (
              <p className="text-xs text-purple-700 mb-1 font-mono">
                AB/XA={data.ratios["AB/XA"]} · BC/AB={data.ratios["BC/AB"]} · CD/BC={data.ratios["CD/BC"]}
              </p>
            )}

            {data.notes?.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5 text-xs text-purple-800 mt-2 space-y-1">
                {data.notes.map((n, i) => <p key={i}>• {n}</p>)}
              </div>
            )}

            {/* Combination breakdown */}
            {data.techniques && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-bold text-zinc-700">{isAr ? "تفصيل التقنيات الخمس" : "Breakdown of 5 techniques"}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(data.techniques).map(([techId, r]) => {
                    const techMeta = { "chart-patterns": "Chart Patterns", "wyckoff": "Wyckoff", "smc": "SMC", "ichimoku": "Ichimoku", "harmonic": "Harmonic" }[techId];
                    const sigBg = r.signal === "BUY" ? "bg-emerald-100 border-emerald-300 text-emerald-700" : r.signal === "SELL" ? "bg-red-100 border-red-300 text-red-700" : "bg-zinc-100 border-zinc-200 text-zinc-600";
                    return (
                      <div key={techId} className={`border rounded-xl p-2 text-xs ${sigBg}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{techMeta}</span>
                          <span className="font-bold">{r.signal} {r.confidence ? `· ${r.confidence}%` : ""}</span>
                        </div>
                        {r.notes && r.notes[0] && <p className="mt-1 opacity-80 text-[10px]">{r.notes[0]}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Box({ label, value, color }) {
  const bg = { zinc: "bg-zinc-50 text-zinc-800", blue: "bg-blue-50 text-blue-700", red: "bg-red-50 text-red-600", emerald: "bg-emerald-50 text-emerald-700" }[color];
  return (
    <div className={`${bg} rounded-xl p-2 text-center`}>
      <p className="text-[10px] text-zinc-500">{label}</p>
      <p className="text-xs sm:text-sm font-mono font-bold">{value}</p>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <Card className="bg-white border-2 border-pink-200 rounded-2xl overflow-hidden">
      <div className={`h-1 bg-${color}-400`} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-5 h-5 text-${color}-500`} />
          <h3 className="font-bold text-sm sm:text-base text-zinc-800">{title}</h3>
        </div>
        <div className="text-xs sm:text-sm text-zinc-700 leading-relaxed">{children}</div>
      </CardContent>
    </Card>
  );
}

export function AdvancedPage({ lang, symbol = "EUR/USD", timeframe = "4h" }) {
  const [active, setActive] = useState("combination");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isAr = lang === "ar";
  const technique = ADVANCED_TECHNIQUES.find((t) => t.id === active);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const urlSymbol = symbol.replace("/", "-");
      const endpoint = active === "combination" ? `advanced-combination/${urlSymbol}` : `advanced-analysis/${active}/${urlSymbol}`;
      const res = await axios.get(`${API}/${endpoint}`, { params: { timeframe } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب التحليل" : "Failed to fetch"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [active, symbol, timeframe]);

  const t = (obj) => obj?.[lang] || obj?.en;
  const Icon = technique?.icon;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="space-y-4">
      <div className="flex items-center gap-2">
        <BookOpen className="w-6 h-6 text-pink-500" />
        <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600">
          {isAr ? "التحليل الفني المتقدم" : "Advanced Technical Analysis"}
        </h1>
      </div>

      <SubNav active={active} setActive={setActive} lang={lang} />

      {technique && (
        <>
          {/* Hero */}
          <Card className="bg-white border-2 border-pink-200 rounded-3xl overflow-hidden">
            <div className={`h-2 bg-gradient-to-r ${technique.color}`} />
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${technique.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl font-extrabold text-pink-600 leading-tight">{t(technique.name)}</h2>
                  <p className="text-xs sm:text-sm text-zinc-600 mt-1">{t(technique.summary)}</p>
                  <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{t(technique.description)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signal */}
          <SignalCard
            data={data}
            loading={loading}
            error={error}
            technique={technique}
            lang={lang}
            onRefresh={fetchData}
            symbol={symbol}
            timeframe={timeframe}
          />

          {/* Info sections */}
          <Section icon={BookOpen} title={isAr ? "القواعد والتعريف" : "Rules & Identification"} color="blue">
            <ul className="space-y-1.5 list-disc list-inside">
              {t(technique.rules).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </Section>

          <Section icon={LogIn} title={isAr ? "شروط الدخول" : "Entry Conditions"} color="green">
            <ul className="space-y-1.5 list-disc list-inside">
              {t(technique.entry).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Section>

          <Section icon={LogOutIcon} title={isAr ? "الخروج / وقف الخسارة" : "Exit / Stop-Loss"} color="red">
            <ul className="space-y-1.5 list-disc list-inside">
              {t(technique.exit).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Section>

          <Section icon={Clock} title={isAr ? "الإطار الزمني الموصى به" : "Recommended Timeframes"} color="purple">
            <div className="flex flex-wrap gap-2">
              {technique.timeframes.map((tf) => (
                <Badge key={tf} className="bg-purple-100 text-purple-700 border-0">{tf}</Badge>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
